import React, {useEffect, useRef, useState} from 'react';
import {Link} from 'react-router-dom';
import {ArrowLeft, X} from 'lucide-react';
import {DirectoryProfile, directoryStatus, membersDirectoryApi} from '../../services/membersDirectoryApi.ts';
import {membersError} from '../../services/membersApi.ts';
import {useTheme} from '../../contexts/ThemeContext.tsx';
import {Card, Loading, Notice, Pagination, primary} from './MembersShared.tsx';
import {PartnerPreview} from './MembersDirectory.tsx';

function PublicationPreview({profile, busy, error, onClose, onConfirm}: {profile: DirectoryProfile; busy: boolean; error: string; onClose: () => void; onConfirm: () => void}) {
  const dialog = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  const pending = useRef(busy);
  useEffect(() => {close.current = onClose; pending.current = busy;}, [onClose, busy]);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog.current?.focus();
    function keydown(event: KeyboardEvent) {
      if (event.key === 'Escape' && !pending.current) close.current();
      if (event.key !== 'Tab') return;
      const elements = dialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled), a[href]');
      if (!elements?.length) {event.preventDefault(); return;}
      const first = elements[0]; const last = elements[elements.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) {event.preventDefault(); last.focus();}
      else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialog.current)) {event.preventDefault(); first.focus();}
    }
    document.addEventListener('keydown', keydown);
    return () => {document.body.style.overflow = overflow; document.removeEventListener('keydown', keydown); previous?.focus();};
  }, []);
  const removing = profile.status === 'published';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div ref={dialog} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="directory-preview-title" className="members-card max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border p-6 outline-none">
        <div className="mb-5 flex items-start justify-between gap-4"><h2 id="directory-preview-title" className="text-lg font-semibold">{removing ? 'Retirar perfil do catálogo' : 'Revisar publicação do perfil'}</h2><button aria-label="Fechar prévia" disabled={busy} onClick={onClose}><X size={20} /></button></div>
        <PartnerPreview partner={profile} detail contact={false} />
        <p className="members-secondary mt-5 break-all text-sm">Contato público: {profile.contact_url}</p>
        <p className="members-secondary my-5 text-sm">{removing ? 'Este perfil deixará de aparecer na busca e sua página pública ficará indisponível.' : 'Os dados acima e o link de contato ficarão visíveis para qualquer visitante. A publicação do perfil não altera a aprovação financeira no Asaas.'}</p>
        {error && <Notice error>{error}</Notice>}
        <div className="mt-5 flex flex-wrap justify-end gap-3"><button className="members-input text-sm" disabled={busy} onClick={onClose}>Cancelar</button><button className={primary} disabled={busy || (!removing && (!profile.consent || !profile.publish_requested))} onClick={onConfirm}>{busy ? 'Aguarde...' : removing ? 'Confirmar retirada' : 'Confirmar publicação'}</button></div>
      </div>
    </div>
  );
}
export default function MembersDirectoryAdmin() {
  const {isDark} = useTheme();
  const [status, setStatus] = useState('pending');
  const [offset, setOffset] = useState(0);
  const [items, setItems] = useState<DirectoryProfile[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [selected, setSelected] = useState<DirectoryProfile | null>(null);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let current = true;
    setLoading(true); setError('');
    void membersDirectoryApi.moderation(status, offset).then(result => {if (current) {setItems(result.items); setTotal(result.total);}}).catch(err => {if (current) setError(membersError(err));}).finally(() => {if (current) setLoading(false);});
    return () => {current = false;};
  }, [status, offset, retry]);
  async function confirm() {
    if (!selected) return;
    setSaving(true); setActionError('');
    try {
      if (selected.status === 'published') await membersDirectoryApi.unpublish(selected);
      else await membersDirectoryApi.publish(selected);
      setSelected(null); setRetry(value => value + 1);
    } catch(err) {setActionError(membersError(err));} finally {setSaving(false);}
  }
  return (
    <div className={`members-theme min-h-screen p-4 sm:p-8 ${isDark ? 'members-theme-dark' : ''}`}>
      <div className="mx-auto max-w-[1120px] space-y-6">
        <Link to="/members/admin" className="members-link inline-flex items-center gap-2 text-sm"><ArrowLeft size={17} />Administração de parceiros</Link>
        <h1 className="text-2xl font-semibold">Catálogo público</h1>
        <p className="members-secondary text-sm">Revise o conteúdo e o consentimento antes de publicar um perfil de serviços. O catálogo é opcional: parceiros que apenas indicam clínicas não precisam aparecer aqui.</p>
        <label className="block max-w-xs space-y-2 text-sm"><span>Situação do perfil</span><select className="members-input w-full" value={status} onChange={event => {setStatus(event.target.value); setOffset(0);}}>{['pending', 'published', 'draft', 'rejected'].map(value => <option key={value} value={value}>{directoryStatus(value)}</option>)}</select></label>
        {loading ? <Loading /> : error ? <Notice error>{error} <button className="underline" onClick={() => setRetry(value => value + 1)}>Tentar novamente</button></Notice> : <>
          {items.length ? <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">{items.map(profile => <Card key={profile.id}><div className="space-y-5"><PartnerPreview partner={profile} contact={false} link={false} /><p className="members-muted text-xs">{directoryStatus(profile.status)} · Versão {profile.revision}</p><button className={primary} onClick={() => {setSelected(profile); setActionError('');}}>{profile.status === 'published' ? 'Revisar retirada' : 'Prévia e publicação'}</button></div></Card>)}</div> : <Card><p className="members-muted py-8 text-center text-sm">Nenhum perfil nesta situação.</p></Card>}
          <Pagination pageSize={12} offset={offset} total={total} onChange={setOffset} />
        </>}
        {selected && <PublicationPreview profile={selected} busy={saving} error={actionError} onClose={() => setSelected(null)} onConfirm={() => void confirm()} />}
      </div>
    </div>
  );
}
